import Button from 'antd/es/button';
import Typography from 'antd/es/typography';
import React from 'react';
import styled from 'styled-components';

const { Title, Text } = Typography;

const EmptyStateContainer = styled.div`
  text-align: center;
  padding: ${({ theme }) => theme.paddingXL}px ${({ theme }) => theme.padding}px;
  color: ${({ theme }) => theme.colorTextSecondary};
`;

const EmptyStateIcon = styled.div`
  font-size: ${({ theme }) => theme.fontSizeXL * 3}px;
  margin-bottom: ${({ theme }) => theme.margin}px;
  opacity: 0.3;
  color: ${({ theme }) => theme.colorTextTertiary};
`;

const DescriptionText = styled(Text)`
  display: block;
  margin-bottom: ${({ theme }) => theme.margin}px;
`;

const DescriptionContainer = styled.div`
  margin-bottom: ${({ theme }) => theme.margin}px;
`;

export interface EmptyStateProps {
  icon: React.ReactNode;
  title: string;
  description?: string | React.ReactNode;
  action?: {
    label: string;
    onClick: () => void;
    type?: 'primary' | 'default';
    icon?: React.ReactNode;
  };
  size?: 'small' | 'default' | 'large';
}

const SizedEmptyStateContainer = styled(EmptyStateContainer)<{
  $size: 'small' | 'default' | 'large';
}>`
  padding: ${({ theme, $size }) => {
    const paddingMap = {
      small: theme.paddingLG,
      default: theme.paddingXL,
      large: theme.paddingXL * 1.5, // Use 1.5x paddingXL instead of non-existent paddingXXL
    };
    return `${paddingMap[$size]}px ${theme.padding}px`;
  }};
`;

const SizedEmptyStateIcon = styled(EmptyStateIcon)<{
  $size: 'small' | 'default' | 'large';
}>`
  font-size: ${({ theme, $size }) => {
    const sizeMultiplier = {
      small: 2,
      default: 3,
      large: 4,
    };
    return `${theme.fontSizeXL * sizeMultiplier[$size]}px`;
  }};
`;

export const EmptyState: React.FC<EmptyStateProps> = ({
  icon,
  title,
  description,
  action,
  size = 'default',
}) => {
  return (
    <SizedEmptyStateContainer $size={size}>
      <SizedEmptyStateIcon $size={size}>{icon}</SizedEmptyStateIcon>
      <Title
        level={size === 'small' ? 5 : 4}
        style={{ margin: '0 0 8px 0', color: 'inherit' }}
      >
        {title}
      </Title>
      {description &&
        (typeof description === 'string' ? (
          <DescriptionText type="secondary">{description}</DescriptionText>
        ) : (
          <DescriptionContainer>{description}</DescriptionContainer>
        ))}
      {action && (
        <Button
          type={action.type || 'primary'}
          icon={action.icon}
          onClick={action.onClick}
          size={size === 'small' ? 'small' : 'middle'}
        >
          {action.label}
        </Button>
      )}
    </SizedEmptyStateContainer>
  );
};

export default EmptyState;
